import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Mail, Check, X, Loader, Eye, EyeOff } from 'lucide-react';

const EmailSettings = ({ user }) => {
  const [providers, setProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [config, setConfig] = useState({
    email_address: '',
    password: '',
    provider: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_use_tls: true,
    imap_host: '',
    imap_port: 993,
    imap_use_ssl: true,
    display_name: '',
    signature: ''
  });

  useEffect(() => {
    loadProviders();
    loadExistingConfig();
  }, []);

  const loadProviders = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/providers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const loadExistingConfig = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.configured) {
          setConfig(prev => ({
            ...prev,
            ...data,
            password: '' // Don't show existing password
          }));
          setSelectedProvider(data.provider);
        }
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    
    if (providers[providerId]) {
      const provider = providers[providerId];
      setConfig(prev => ({
        ...prev,
        provider: providerId,
        smtp_host: provider.smtp_host,
        smtp_port: provider.smtp_port,
        smtp_use_tls: provider.smtp_use_tls,
        imap_host: provider.imap_host,
        imap_port: provider.imap_port,
        imap_use_ssl: provider.imap_use_ssl
      }));
    }
  };

  const testConnection = async () => {
    if (!config.email_address || !config.password) {
      toast.error('Bitte Email-Adresse und Passwort eingeben');
      return;
    }

    setTesting(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setTesting(false);
    }
  };

  const saveConfig = async () => {
    if (!config.email_address || !config.password) {
      toast.error('Bitte Email-Adresse und Passwort eingeben');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/email/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Email-Einstellungen</h1>
          <p className="text-gray-600 mt-2">Konfigurieren Sie Ihren Email-Account für den Versand und Empfang von Emails</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200 bg-blue-50">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Email-Zugang</h2>
                <p className="text-sm text-gray-600">Wählen Sie Ihren Email-Provider und geben Sie Ihre Zugangsdaten ein</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Email-Provider auswählen</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(providers).map(([key, provider]) => (
                  <button
                    key={key}
                    onClick={() => handleProviderChange(key)}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      selectedProvider === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{provider.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email-Adresse *</label>
              <input
                type="email"
                value={config.email_address}
                onChange={(e) => setConfig({...config, email_address: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ihre@email.de"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Passwort / App-Passwort *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={config.password}
                  onChange={(e) => setConfig({...config, password: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Für Gmail, Outlook, etc. verwenden Sie bitte ein App-Passwort (nicht Ihr normales Passwort)
              </p>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Anzeigename (optional)</label>
              <input
                type="text"
                value={config.display_name}
                onChange={(e) => setConfig({...config, display_name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ihr Name oder Firma"
              />
            </div>

            {/* Signature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email-Signatur (optional)</label>
              <textarea
                value={config.signature}
                onChange={(e) => setConfig({...config, signature: e.target.value})}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Mit freundlichen Grüßen&#10;Ihr Team"
              />
            </div>

            {/* Advanced Settings (Collapsible) */}
            <details className="border border-gray-200 rounded-lg p-4">
              <summary className="font-medium text-gray-700 cursor-pointer">Erweiterte Einstellungen</summary>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                    <input
                      type="text"
                      value={config.smtp_host}
                      onChange={(e) => setConfig({...config, smtp_host: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                    <input
                      type="number"
                      value={config.smtp_port}
                      onChange={(e) => setConfig({...config, smtp_port: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IMAP Host</label>
                    <input
                      type="text"
                      value={config.imap_host}
                      onChange={(e) => setConfig({...config, imap_host: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">IMAP Port</label>
                    <input
                      type="number"
                      value={config.imap_port}
                      onChange={(e) => setConfig({...config, imap_port: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </details>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={testConnection}
                disabled={testing}
                className="flex-1 px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {testing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Teste Verbindung...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Verbindung testen
                  </>
                )}
              </button>
              <button
                onClick={saveConfig}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Help Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-3">📧 Hilfe: App-Passwörter erstellen</h3>
          <div className="space-y-3 text-sm text-blue-800">
            <div>
              <strong>Gmail:</strong> Google-Konto → Sicherheit → 2-Faktor-Authentifizierung → App-Passwörter
            </div>
            <div>
              <strong>Outlook/Office 365:</strong> Microsoft-Konto → Sicherheit → App-Passwörter
            </div>
            <div>
              <strong>Yahoo:</strong> Account-Info → Kontosicherheit → App-Passwörter generieren
            </div>
            <div>
              <strong>Andere Provider:</strong> Meist unter Sicherheitseinstellungen oder Email-Einstellungen
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailSettings;
